import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Heading,
  VStack,
  HStack,
  Text,
  Input,
  Grid,
  Card,
  Field,
  Tabs,
  Toaster,
  createToaster,
} from '@chakra-ui/react'
import { Save, Upload } from 'lucide-react'
import { useAgency, useAgencyConfig, useUpdateAgency, uploadAgencyAsset } from '../hooks/useAgency'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { AgencyLogo } from '../components/AgencyLogo'
import { useQueryClient } from '@tanstack/react-query'
import { generateColorPalette } from '@/shared/utils/colorPalette'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

/**
 * AgencySettingsPage - Admin page to configure agency branding
 *
 * Features:
 * - Change brand colors with color pickers
 * - Upload logos (compact, inline)
 * - Upload login background image
 * - Preview changes in real-time
 */
export function AgencySettingsPage() {
  const { data: agency, isLoading, error } = useAgency()
  const { data: agencyConfig } = useAgencyConfig()
  const updateAgency = useUpdateAgency()
  const queryClient = useQueryClient()

  // Local state for primary color (brand.500 - median)
  const [primaryColor, setPrimaryColor] = useState(agency?.brand_primary_color || '#10B981')

  // Generate color palette preview from primary color
  const colorPalette = useMemo(() => generateColorPalette(primaryColor), [primaryColor])

  // Local state for agency info
  const [agencyInfo, setAgencyInfo] = useState({
    name: agency?.name || '',
    slug: agency?.slug || '',
  })

  // Sync state when agency data loads
  useEffect(() => {
    if (agency) {
      setPrimaryColor(agency.brand_primary_color || '#059669')
      setAgencyInfo({
        name: agency.name || '',
        slug: agency.slug || '',
      })
    }
  }, [agency])

  const handleSaveColors = async () => {
    if (!agency) return

    try {
      await updateAgency.mutateAsync({
        id: agency.id,
        brand_primary_color: primaryColor,
      })
      toaster.create({
        title: 'Succès',
        description: 'Couleur mise à jour ! Rafraîchissez la page pour voir les changements.',
        type: 'success',
        duration: 5000,
      })
    } catch (error) {
      console.error('Error updating color:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour de la couleur',
        type: 'error',
        duration: 5000,
      })
    }
  }

  const handleSaveInfo = async () => {
    if (!agency) return

    try {
      await updateAgency.mutateAsync({
        id: agency.id,
        name: agencyInfo.name,
        slug: agencyInfo.slug,
      })
      toaster.create({
        title: 'Succès',
        description: 'Informations mises à jour avec succès !',
        type: 'success',
        duration: 5000,
      })
    } catch (error) {
      console.error('Error updating agency info:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour des informations',
        type: 'error',
        duration: 5000,
      })
    }
  }

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'logo-compact' | 'logo-inline' | 'login-background'
  ) => {
    if (!agency) return
    const file = event.target.files?.[0]
    if (!file) return

    try {
      // Add timestamp to URL to bust cache
      const url = await uploadAgencyAsset(agency.id, file, type)
      const urlWithTimestamp = `${url}?t=${Date.now()}`

      // Update agency with new URL
      const fieldName =
        type === 'logo-compact'
          ? 'logo_compact_url'
          : type === 'logo-inline'
          ? 'logo_inline_url'
          : 'login_background_url'

      await updateAgency.mutateAsync({
        id: agency.id,
        [fieldName]: urlWithTimestamp,
      })

      // Invalidate queries to force refresh
      await queryClient.invalidateQueries({ queryKey: ['agencies'] })

      toaster.create({
        title: 'Succès',
        description: 'Logo uploadé avec succès !',
        type: 'success',
        duration: 5000,
      })

      // Reset the input to allow re-uploading the same file
      event.target.value = ''
    } catch (error) {
      console.error('Error uploading logo:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Erreur lors de l\'upload du logo',
        type: 'error',
        duration: 5000,
      })
    }
  }

  if (isLoading) {
    return <LoadingState message="Chargement de la configuration..." />
  }

  if (error || !agency) {
    return <ErrorState message="Impossible de charger la configuration de l'agence" />
  }

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">Configuration de l'agence</Heading>
      </HStack>

      <Tabs.Root defaultValue="branding">
        <Tabs.List>
          <Tabs.Trigger value="branding">Identité visuelle</Tabs.Trigger>
          <Tabs.Trigger value="info">Informations</Tabs.Trigger>
        </Tabs.List>

        <Box mt={4}>
          <Tabs.Content value="branding">
            <VStack align="stretch" gap={6}>
              {/* Logos Section */}
              <Card.Root>
                <Card.Header>
                  <Heading size="md">Logos</Heading>
                </Card.Header>
                <Card.Body>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
                    {/* Logo Compact */}
                    <VStack align="stretch" gap={3}>
                      <Text fontWeight="medium">Logo compact</Text>
                      <Box
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        bg="gray.50"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minH="120px"
                      >
                        <AgencyLogo variant="compact" height="60px" />
                      </Box>
                      <Field.Root>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(e, 'logo-compact')}
                          display="none"
                          id="logo-compact-upload"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          w="full"
                          onClick={() => document.getElementById('logo-compact-upload')?.click()}
                        >
                          <Upload size={16} />
                          Changer
                        </Button>
                      </Field.Root>
                    </VStack>

                    {/* Logo Inline */}
                    <VStack align="stretch" gap={3}>
                      <Text fontWeight="medium">Logo inline</Text>
                      <Box
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        bg="gray.50"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minH="120px"
                      >
                        <AgencyLogo variant="inline" height="60px" />
                      </Box>
                      <Field.Root>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(e, 'logo-inline')}
                          display="none"
                          id="logo-inline-upload"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          w="full"
                          onClick={() => document.getElementById('logo-inline-upload')?.click()}
                        >
                          <Upload size={16} />
                          Changer
                        </Button>
                      </Field.Root>
                    </VStack>

                    {/* Login Background */}
                    <VStack align="stretch" gap={3}>
                      <Text fontWeight="medium">Image de fond login</Text>
                      <Box
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        bg="gray.50"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minH="120px"
                        backgroundImage={agencyConfig?.assets.loginBackgroundUrl || 'none'}
                        backgroundSize="cover"
                        backgroundPosition="center"
                      >
                        {!agencyConfig?.assets.loginBackgroundUrl && (
                          <Text fontSize="sm" color="gray.500">
                            Aucune image
                          </Text>
                        )}
                      </Box>
                      <Field.Root>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(e, 'login-background')}
                          display="none"
                          id="login-bg-upload"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          w="full"
                          onClick={() => document.getElementById('login-bg-upload')?.click()}
                        >
                          <Upload size={16} />
                          Changer
                        </Button>
                      </Field.Root>
                    </VStack>
                  </Grid>
                </Card.Body>
              </Card.Root>

              {/* Colors Section */}
              <Card.Root>
                <Card.Header>
                  <Heading size="md">Couleur de la marque</Heading>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Choisissez votre couleur principale. Les autres nuances sont générées automatiquement.
                  </Text>
                </Card.Header>
                <Card.Body>
                  <VStack align="stretch" gap={6}>
                    {/* Primary Color Picker */}
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={3}>
                        Couleur primaire (500 - médiane)
                      </Text>
                      <HStack gap={4} align="flex-start">
                        <Box>
                          <Input
                            type="color"
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            w="80px"
                            h="80px"
                            p={1}
                            cursor="pointer"
                          />
                        </Box>
                        <VStack align="stretch" flex={1}>
                          <Input
                            type="text"
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            fontFamily="mono"
                            placeholder="#059669"
                            maxW="200px"
                          />
                          <Text fontSize="xs" color="gray.500">
                            Format hexadécimal (ex: #059669)
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>

                    {/* Generated Palette Preview */}
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={3}>
                        Aperçu de la palette générée
                      </Text>
                      <Grid templateColumns={{ base: 'repeat(5, 1fr)', md: 'repeat(11, 1fr)' }} gap={2}>
                        {Object.entries(colorPalette).map(([shade, color]) => (
                          <VStack key={shade} align="stretch" gap={1}>
                            <Box
                              w="full"
                              h="60px"
                              borderRadius="md"
                              bg={color}
                              borderWidth="2px"
                              borderColor={shade === '500' ? 'gray.800' : 'gray.200'}
                              position="relative"
                            >
                              {shade === '500' && (
                                <Box
                                  position="absolute"
                                  bottom={1}
                                  left="50%"
                                  transform="translateX(-50%)"
                                  bg="gray.800"
                                  color="white"
                                  px={1}
                                  borderRadius="sm"
                                  fontSize="9px"
                                  fontWeight="bold"
                                >
                                  PRIMARY
                                </Box>
                              )}
                            </Box>
                            <Text fontSize="xs" textAlign="center" fontWeight="medium">
                              {shade}
                            </Text>
                            <Text fontSize="10px" textAlign="center" fontFamily="mono" color="gray.600">
                              {color.toUpperCase()}
                            </Text>
                          </VStack>
                        ))}
                      </Grid>
                    </Box>

                    <HStack justify="flex-end" mt={4}>
                      <Button
                        colorPalette="brand"
                        onClick={handleSaveColors}
                        loading={updateAgency.isPending}
                      >
                        <Save size={16} />
                        Enregistrer la couleur
                      </Button>
                    </HStack>
                  </VStack>
                </Card.Body>
              </Card.Root>

              {/* Preview Section */}
              <Card.Root>
                <Card.Header>
                  <Heading size="md">Aperçu</Heading>
                </Card.Header>
                <Card.Body>
                  <VStack align="stretch" gap={4}>
                    <HStack gap={3}>
                      <Button colorPalette="brand" size="sm">
                        Bouton primaire
                      </Button>
                      <Button variant="outline" colorPalette="brand" size="sm">
                        Bouton outline
                      </Button>
                      <Button variant="ghost" colorPalette="brand" size="sm">
                        Bouton ghost
                      </Button>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      Note : Rafraîchissez la page après avoir enregistré pour voir les changements
                      appliqués partout dans l'application.
                    </Text>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </VStack>
          </Tabs.Content>

          <Tabs.Content value="info">
            <Card.Root>
              <Card.Header>
                <Heading size="md">Informations de l'agence</Heading>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Modifiez le nom et le slug de votre agence
                </Text>
              </Card.Header>
              <Card.Body>
                <VStack align="stretch" gap={4}>
                  <Field.Root>
                    <Field.Label>Nom de l'agence</Field.Label>
                    <Input
                      value={agencyInfo.name}
                      onChange={(e) => setAgencyInfo((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Nom de l'agence"
                    />
                    <Field.HelperText>
                      Le nom de votre agence tel qu'il apparaîtra dans l'interface
                    </Field.HelperText>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Slug</Field.Label>
                    <Input
                      value={agencyInfo.slug}
                      onChange={(e) => setAgencyInfo((prev) => ({ ...prev, slug: e.target.value }))}
                      placeholder="slug-de-lagence"
                      fontFamily="mono"
                    />
                    <Field.HelperText>
                      Identifiant unique pour les URLs (ex: /login/mon-agence). Utilisez uniquement des
                      lettres minuscules, chiffres et tirets.
                    </Field.HelperText>
                  </Field.Root>

                  <HStack justify="flex-end" mt={4}>
                    <Button
                      colorPalette="brand"
                      onClick={handleSaveInfo}
                      loading={updateAgency.isPending}
                    >
                      <Save size={16} />
                      Enregistrer les informations
                    </Button>
                  </HStack>
                </VStack>
              </Card.Body>
            </Card.Root>
          </Tabs.Content>
        </Box>
      </Tabs.Root>
      <Toaster toaster={toaster}>
        {(toast) => (
          <Box
            bg={toast.type === 'success' ? 'green.500' : toast.type === 'error' ? 'red.500' : 'blue.500'}
            color="white"
            p={4}
            borderRadius="md"
            boxShadow="lg"
          >
            <VStack align="start" gap={1}>
              {toast.title && <Text fontWeight="bold">{toast.title}</Text>}
              {toast.description && <Text fontSize="sm">{toast.description}</Text>}
            </VStack>
          </Box>
        )}
      </Toaster>
    </Box>
  )
}
